import { useEffect } from 'react';
import { useCountdown } from 'usehooks-ts';

/**
 * Custom hook that provides a countdown timer functionality.
 *
 * @param countdownFrom - The starting point of the countdown timer. It can be a string, number, Date object
 * @param updateIntervalMs - The interval in milliseconds at which the countdown timer should be updated. Default is 1000ms (1 second).
 * @returns An object containing the current count of the countdown timer and the count start.
 */
const useCountdownTimer = (
  countdownFrom: string | number | Date,
  updateIntervalMs = 1000,
) => {
  const fromDatetime = new Date(countdownFrom).getTime();
  const datetimeNow = Date.now();

  const dateTimeCountDownSeconds = !countdownFrom
    ? 0
    : Math.ceil((fromDatetime - datetimeNow) / 1000);

  const [count, { startCountdown, stopCountdown }] = useCountdown({
    countStart: dateTimeCountDownSeconds,
    intervalMs: updateIntervalMs,
  });

  const countShouldBeZero = count < 0 || datetimeNow > fromDatetime;

  useEffect(() => {
    if (countShouldBeZero) {
      stopCountdown();
    } else {
      startCountdown();
    }
  }, [countShouldBeZero, startCountdown, stopCountdown, countdownFrom]);

  return {
    count: countShouldBeZero ? 0 : count,
    countStart: dateTimeCountDownSeconds,
  };
};

export default useCountdownTimer;
